import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import RecordRTC from "recordrtc";
import './VideoRecorder.css';
import { v4 as uuidv4 } from 'uuid';
import CryptoService from "./CyrptoService";

// Constants
const MAX_RECORD_TIME = 7;
const COUNTDOWN_START = 3;
const FACE_MATCH_THRESHOLD = 80;
const RETRY_DELAY = 5000;

// Device detection utility (memoized outside component)
const isIOSDevice = /iPad|iPhone|iPod/i.test(navigator.userAgent);

const VideoComponent = ({ setSuccess, setFlowDoc, documentImage, setFaceNotmatchStartAgain, requestId, clientId, showAlert }) => {
    // UI flow states
    const [uniqueId] = useState(uuidv4());
    const [isLoading, setIsLoading] = useState(false);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_START);
    const [isRecording, setIsRecording] = useState(false);
    const [recordedVideoUrl, setRecordedVideoUrl] = useState("");
    const [faceSnapshotBase64, setFaceSnapshotBase64] = useState("");
    const [illustrations, setIllustrations] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState([]);
    const [location, setLocation] = useState({ lat: null, long: null });
    const [recordTime, setRecordTime] = useState(0);
    const [apiTranscript, setApiTranscript] = useState("");
    const [isSendingApi, setIsSendingApi] = useState(false);
    const [isFallback, setIsFallback] = useState(false);
    const [base64Video, setBase64Video] = useState('');
    const [randomNumber, setRandomNumber] = useState(0);
    const [startAgianHit, setStartagainHit] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [refId, setRefId] = useState('');

    // Refs for video element and media objects
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const timerRef = useRef(null);
    const intervalRef = useRef(null);

    // Memoized utility functions
    const formatTime = useCallback((sec) => (sec < 10 ? `0${sec}` : sec), []);

    const generateRandomNumber = useCallback(() => {
        return Math.floor(Math.random() * (999 - 100 + 1)) + 100;
    }, []);

    // Initialize component
    useEffect(() => {
        // Check MediaRecorder support
        if (
            typeof MediaRecorder === "undefined" ||
            !MediaRecorder.isTypeSupported("video/webm; codecs=vp8,opus")
        ) {
            console.warn("Native MediaRecorder not supported; using RecordRTC fallback.");
            setIsFallback(true);
        }

        // Get user_id from URL
        const params = new URLSearchParams(window.location.search);
        const user_id = params.get("user_id") || '';
        setRefId(user_id);

        // Generate initial random number
        setRandomNumber(generateRandomNumber());

        // Capture geolocation
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        lat: position.coords.latitude,
                        long: position.coords.longitude,
                    });
                },
                (error) => {
                    console.error("Error fetching location:", error);
                }
            );
        } else {
            console.error("Geolocation is not supported by this browser.");
        }
    }, [generateRandomNumber]);

    // Memoized helper functions
    const captureFaceSnapshot = useCallback(() => {
        if (videoRef.current) {
            const canvas = document.createElement("canvas");
            canvas.width = videoRef.current.videoWidth || 640;
            canvas.height = videoRef.current.videoHeight || 480;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL("image/jpeg").split(",")[1];
        }
        return "";
    }, []);

    const stopRecording = useCallback(() => {
        if (!recorderRef.current) return;

        const { audioRecorder, videoRecorder } = recorderRef.current;

        if (videoRecorder?.state !== "inactive") {
            videoRecorder.stop();
        }

        if (audioRecorder?.state !== "inactive") {
            audioRecorder.stop();
        }

        setIsRecording(false);
    }, []);

    // Start or stop recording based on current state
    const handleCaptureClick = useCallback(async () => {
        setIllustrations(false);
        if (!isRecording && !isCountingDown && !recordedVideoUrl) {
            setIsLoading(true);
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: true,
                });
                streamRef.current = stream;

                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.muted = true;
                    videoRef.current.play();

                    if (isIOSDevice) {
                        videoRef.current.setAttribute('playsinline', 'true');
                        videoRef.current.setAttribute('controls', 'true');
                    }
                }

                setIsLoading(false);
                setCountdown(COUNTDOWN_START);
                setIsCountingDown(true);

            } catch (error) {
                console.error("Error accessing camera:", error);
                setIsLoading(false);
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    showAlert('Camera and microphone permissions are required to record the video.');
                }
            }
        } else if (isRecording) {
            stopRecording();
        }
    }, [isRecording, isCountingDown, recordedVideoUrl, stopRecording]);

    // API and helper functions (defined before sendVideoToApi)
    const lmbd_ovd_step_status = useCallback(() => {
        const obj = {
            request_id: requestId,
            is_document_uploaded: true,
            is_ipv_done: true,
            is_face_match_done: true
        };

        const encrypted = CryptoService.encrypt(obj);

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "data": encrypted,
            "source_id": clientId
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        fetch("https://z33q3r9qqd.execute-api.ap-south-1.amazonaws.com/default/step_status", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                CryptoService.decrypt(result.data);
                const requestDataToSend = {
                    action: 'ovd Verification Completed',
                    data: {
                        client_id: clientId,
                        requestId: requestId
                    }
                };
                window.parent.postMessage(requestDataToSend, "*");
            })
            .catch((error) => console.error(error));
    }, [requestId, clientId]);

    const UploadDoc = useCallback((base64Videor) => {
        setLoadingMessage(['Uploading video document, please wait...']);

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "source_id": clientId,
            "reqid": requestId,
            "doctype": "video",
            "base64": base64Videor,
            "filetype": "mp4",
            "ovdresponse": "successfully uploaded video"
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        fetch("https://uos01m6pv3.execute-api.ap-south-1.amazonaws.com/default/lmbd_ovd_document_upload", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                lmbd_ovd_step_status();
                setSuccess(true);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [clientId, requestId, setSuccess, lmbd_ovd_step_status]);

    const handleRetake = useCallback(async () => {
        setRecordedVideoUrl("");
        setBase64Video("");
        setRecordTime(0);
        setApiTranscript("");
        setRandomNumber(generateRandomNumber());
        setHasError(false);
        setStartagainHit(false);
        setLoadingMessage([]);

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
        }

        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.srcObject = null;
            videoRef.current.removeAttribute("src");
            videoRef.current.load();
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true,
            });
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.muted = true;
                videoRef.current.setAttribute("playsinline", "true");
                await videoRef.current.play();
            }

            setCountdown(COUNTDOWN_START);
            setIsCountingDown(true);
        } catch (error) {
            console.error("Error restarting camera:", error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                showAlert('Camera and microphone permissions are required to record the video.');
            }
        }

        handleCaptureClick();
    }, [generateRandomNumber, handleCaptureClick]);



    const funFaceMatch = useCallback(async (faceBase64, base64Videor) => {
        const cleanData = documentImage.replace(/^b'|'/g, '');
        setLoadingMessage(['Matching face with the document']);

        const payload1 = {
            DocImage: cleanData,
            SelfieImage: faceBase64,
        };

        const errorMessages = [
           'We could not perform the face matching operation due to one of the following reasons:',
            'The face does not match the document.',
            'The face or the document image is not clear.',
        ];

        try {
            const response = await fetch("https://7izf2fny08.execute-api.ap-south-1.amazonaws.com/default/facematchservice", {
                method: "POST",
                headers: {
                    "x-api-key": "jCSIISHVNG2e3QLKbX0P82l6LyFM4oLj3mAyUH5R",
                    "authorizationToken": "7Qeb3IoX5pxc214L",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload1),
            });

            const data = await response.json();

            if (data.confidence_score > FACE_MATCH_THRESHOLD && !hasError) {
                UploadDoc(base64Videor);
            } else {
                setHasError(true);
                setLoadingMessage(errorMessages);
                setStartagainHit(true);
                setIsSendingApi(false);
            }
        } catch (error) {
            setHasError(true);
            console.error("Error sending video:", error);
            setLoadingMessage(errorMessages);
            setStartagainHit(true);
            setIsSendingApi(false);
        }
    }, [documentImage, hasError, UploadDoc]);

    // Helper function to send video blob to API
    const sendVideoToApi = useCallback((blob, faceBase64, base64Videor) => {
        setBase64Video(base64Videor);
        setIsSendingApi(true);
        setLoadingMessage(['Please wait, we are analyzing your recorder video...']);

        const reader = new FileReader();
        reader.onloadend = () => {
            const payload = {
                device: isIOSDevice ? "ios" : "android",
                bas64string: reader.result?.toString().split(',')[1],
            };

            fetch("https://wnawjozykj.execute-api.ap-south-1.amazonaws.com/default/speech_to_text_test", {
                method: "POST",
                headers: {
                    "x-api-key": "li0ZNckous14N6JCsq3pk4bVqtavmGHV7ZXOZcFm",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.data === "") {
                        setLoadingMessage([`${data.message}`])
                        setApiTranscript("No audio detected. Please try again.")
                        setIsSendingApi(false)
                    } else if (data.data === randomNumber.toString()) {
                        funFaceMatch(faceBase64, base64Videor);
                    } else {
                        setLoadingMessage([`You said: ${data.data} OTP did not match`]);
                        setApiTranscript(data.transcript?.transcript || "Invalid OTP, Please try again.");
                        setIsSendingApi(false);
                    }
                })
                .catch((error) => {
                    console.error("Error sending video:", error);
                    setIsSendingApi(false);
                });
        };
        reader.readAsDataURL(blob);
    }, [randomNumber, funFaceMatch]);

    const startRecording = useCallback((faceBase64) => {
        setRecordTime(0);

        if (!streamRef.current) return;

        const audioStream = new MediaStream();
        const videoStream = new MediaStream();

        streamRef.current.getTracks().forEach(track => {
            if (track.kind === "audio") audioStream.addTrack(track);
            if (track.kind === "video") videoStream.addTrack(track);
        });

        if (isFallback) {
            recorderRef.current = RecordRTC(audioStream, {
                type: "audio",
                mimeType: "audio/wav",
                recorderType: RecordRTC.MediaStreamRecorder,
            });
            recorderRef.current.startRecording();
            setIsRecording(true);
            return;
        }

        let audioChunks = [];
        let videoChunks = [];

        try {
            const videoRecorder = new MediaRecorder(streamRef.current, {
                mimeType: "video/webm; codecs=vp8,opus",
            });

            const audioRecorder = new MediaRecorder(audioStream, {
                mimeType: "audio/webm; codecs=opus",
            });

            videoRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) videoChunks.push(e.data);
            };

            audioRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunks.push(e.data);
            };

            audioRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
                const videoBlob = new Blob(videoChunks, { type: "video/webm" });

                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64Videor = reader.result.split(",")[1] || "";
                    sendVideoToApi(audioBlob, faceBase64, base64Videor);
                };
                reader.readAsDataURL(videoBlob);
            };

            videoRecorder.start();
            audioRecorder.start();

            recorderRef.current = { audioRecorder, videoRecorder };
            setIsRecording(true);

        } catch (e) {
            console.error("MediaRecorder failed:", e);
            setIsFallback(true);
            startRecording(faceBase64);
        }
    }, [isFallback, sendVideoToApi]);

    // Countdown effect
    useEffect(() => {
        if (hasError || loadingMessage.length > 0) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        if (isCountingDown && countdown > 0) {
            timerRef.current = setTimeout(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        } else if (isCountingDown && countdown === 0) {
            setIsCountingDown(false);
            const faceBase64 = captureFaceSnapshot();
            setFaceSnapshotBase64(faceBase64);
            startRecording(faceBase64);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isCountingDown, countdown, hasError, loadingMessage, captureFaceSnapshot, startRecording]);

    // Recording timer effect
    useEffect(() => {
        if (hasError || loadingMessage.length > 0) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        if (isRecording && recordTime < MAX_RECORD_TIME) {
            intervalRef.current = setInterval(() => {
                setRecordTime((prev) => prev + 1);
            }, 1000);
        }

        if (recordTime === MAX_RECORD_TIME) {
            stopRecording();
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isRecording, recordTime, hasError, loadingMessage, stopRecording]);

    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current) {
            // Time update handler - kept for potential future use
        }
    }, []);

    const handleStartAgian = useCallback(() => {
        setHasError(false);
        setStartagainHit(false);
        videoRef.current = null;
        streamRef.current = null;
        recorderRef.current = null;
        setFlowDoc('DocumentUpload');
    }, [setFlowDoc]);

    // Memoized computed values
    const formattedRecordTime = useMemo(() => formatTime(recordTime), [recordTime, formatTime]);
    const formattedMaxTime = useMemo(() => formatTime(MAX_RECORD_TIME), [formatTime]);
    const videoDisplayStyle = useMemo(() => ({
        width: '100%',
        height: '100%',
        clipPath: 'circle(150px at center)',
        display: isRecording ? 'block' : 'none',
        transform: 'scaleX(-1)',
        border: '1px solid red',
        marginRight: "1rem"
    }), [isRecording]);

    const containerGradient = useMemo(() => ({
        background: 'linear-gradient(0deg, rgba(255,219,169,1) 3%, rgba(255,236,209,1) 50%, rgba(255,240,219,1) 70%)'
    }), []);

    // console.log('lo',loadingMessage);
    
    return (
        <div className="video-recorder-container">
            <div className="video-recorder-main" style={containerGradient}>

                <div style={{ width: "100%", position: "relative", marginTop: '4px' }}>
                    <div
                        style={{
                            height: "1.5px",
                            width: "100%",
                            background: "#ccc",
                            borderRadius: "2px",
                            position: "relative",
                        }}
                    >
                        <div
                            style={{
                                height: "1.5px",
                                width: "50%",
                                background: "#007bff",
                                borderRadius: "2px",
                                position: "absolute",
                            }}
                        ></div>
                        <div
                            style={{
                                height: "8px",
                                width: "8px",
                                background: "#007bff",
                                borderRadius: "50%",
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                transform: "translate(-50%, -50%)",
                            }}
                        ></div>
                    </div>
                </div>

                <h2 className="video-recorder-heading" style={{ color: '#464f82', fontSize: '15px' }}>
                    OVD Document Verification
                </h2>
                <div>
                    <p className="video-recorder-declaration" style={{ fontSize: '13px' }}>
                        Read out the OTP written on your screen by recording a video
                    </p>
                    <p className="video-recorder-declaration-text">
                        00:{formattedRecordTime} / 00:{formattedMaxTime}
                    </p>
                </div>


                {/* Video container */}
                <div className="video-recorder-video-container" style={{ maxHeight: "400px" }}>
                    {/* Blinking countdown in the center of the video */}
                    {isCountingDown && (
                        <div>
                            <div className="video-recorder-countdown" >
                                {countdown}
                            </div>
                        </div>
                    )}


                    <div style={{ position: 'relative', width: "100%", height: "100%" }}>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            onTimeUpdate={handleTimeUpdate}
                            style={videoDisplayStyle}
                        />

                        {isRecording && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "20%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    color: "white",
                                    padding: "5px 10px",
                                    borderRadius: "5px",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                    zIndex: 10,
                                }}
                            >
                                {randomNumber}
                            </div>
                        )}
                    </div>

                    {/* Illustrations */}
                    {
                        illustrations &&
                        <>
                            <div style={{ width: '90%', margin: 'auto', position: 'absolute', top: 0, background: "white", padding: 20, borderRadius: '10px' }}>
                                <div style={{ height: '10%', marginLeft: '20px' }}>
                                    <h3 style={{ fontFamily: 'sans-serif', fontSize: '12px' }}>DOs and DON'Ts</h3>
                                </div>
                                <div>
                                    <ul style={{ fontFamily: 'sans-serif', fontSize: '12px', lineHeight: '1.6' }}>
                                        <li >📽️ Record a video</li>
                                        <li >🎶 No background noise</li>
                                        <li >🙂 Face must be clearly visible</li>
                                        <li >💁 Voice must be clear and not muffled</li>
                                        <li >😎 Remove any accessories</li>
                                    </ul>
                                </div>
                            </div>
                        </>
                    }

                    {loadingMessage.length > 0 && (
                        <div
                            style={{
                                position: 'absolute',
                                bottom: '40%',
                                width: '98%',
                                display: 'flex',
                                justifyContent: 'center',
                                zIndex: 999
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: 'rgba(222, 217, 217, 0.45)',
                                    padding: 8,
                                    borderRadius: '10px',
                                }}
                            >
                                <div style={{ textAlign: 'left', padding: '0 5px' }}>
                                    {loadingMessage.map((msg, index) => {
                                        if (msg.includes("We could not perform the face matching operation")) {
                                            return <div key={index} style={{ color: 'red', marginBottom: '5px', fontWeight: '500' }}>{msg}</div>;
                                        }
                                        return (
                                            <div key={index} style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px' }}>
                                                {msg}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}


                    {!recordedVideoUrl && illustrations && (
                        <div style={{
                            position: 'absolute', bottom: 0, left: '50%',
                            transform: 'translateX(-50%)',
                        }}>
                            <button
                                onClick={handleCaptureClick}
                                className="video-recorder-capture-button"
                                style={{
                                    width: "10rem",
                                    height: '40px',
                                    border: '1px solid rgb(188, 39, 39)',
                                    backgroundColor: "rgb(186, 0, 0)",
                                    cursor: "pointer",
                                    padding: 2,
                                    color: 'white'
                                }}
                            >
                                Start Recording
                            </button>
                        </div>
                    )}

                    {startAgianHit && (
                        <div style={{
                            position: 'absolute', bottom: 0, left: '50%',
                            transform: 'translateX(-50%)',
                        }}>
                            <button
                                onClick={handleStartAgian}
                                className="video-recorder-capture-button"
                                style={{
                                    width: "15rem",
                                    height: '40px',
                                    border: '1px solid rgb(188, 39, 39)',
                                    backgroundColor: "rgb(174, 1, 1)",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    padding: 2,
                                    color: 'white'
                                }}
                            >
                                Start Again
                            </button>
                        </div>
                    )}

                    {apiTranscript && (
                        <div style={{ position: 'absolute', left: '50%', bottom: '25%', transform: 'translate(-50%, 50%)' }}>
                            <div className="video-recorder-transcript">
                                <p>{apiTranscript}</p>
                            </div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <button
                                    onClick={handleRetake}
                                    style={{
                                        width: '7rem',
                                        height: '40px',
                                        border: "1.5px solid #a3cef1",
                                        backgroundColor: "#e7e4ff",
                                        borderRadius: "10px",
                                        cursor: "pointer",
                                        fontSize: "16px",
                                    }}
                                >
                                    Retake
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Keyframes for blink animation */}
            <style>
                {`
                    @keyframes blink {
                        0% { opacity: 1; }
                        50% { opacity: 0.2; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
        </div>
    );
};

export default VideoComponent;